import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Chip, MenuItem, Select,
  FormControl, InputLabel, SelectChangeEvent, Grid, Snackbar, Alert,
} from '@mui/material';
import { Add, Edit, Delete, Warning, Upload, MyLocation, Clear, OpenInNew, Description } from '@mui/icons-material';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../../api/client';

interface LocationRecord {
  id: number; name: string; category: string; address: string; apt_suite: string;
  city: string; state: string; zip_code: string; phone: string; latitude: number | null;
  longitude: number | null; hazard_info: string; additional_notes: string;
  is_active: boolean;
}

interface LocationDocument {
  id: number; location_id: number; category: string; notes: string;
  original_filename: string; content_type: string; size_bytes: number;
  uploaded_by: string; uploaded_at: string; view_url: string;
}

const CATEGORIES = [
  'business', 'residence', 'school', 'government', 'hospital', 'church',
  'park', 'intersection', 'apartment_complex', 'hotel', 'other',
];

const DOC_CATEGORIES: { value: string; label: string }[] = [
  { value: 'building_diagram', label: 'Building Diagram' },
  { value: 'hazard_plan', label: 'Hazard Plan' },
  { value: 'emergency_contacts', label: 'Emergency Contacts' },
  { value: 'other', label: 'Other' },
];
const DOC_CATEGORY_LABELS: Record<string, string> = Object.fromEntries(
  DOC_CATEGORIES.map((c) => [c.value, c.label]),
);

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function LocationsPage() {
  const [locations, setLocations] = useState<LocationRecord[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<LocationRecord | null>(null);
  const [form, setForm] = useState({
    name: '', category: 'other', address: '', apt_suite: '', city: '', state: '',
    zip_code: '', phone: '', latitude: '', longitude: '', hazard_info: '', additional_notes: '',
  });

  const [snack, setSnack] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  // Premise documents state (edit dialog)
  const [documents, setDocuments] = useState<LocationDocument[]>([]);
  const [docCategory, setDocCategory] = useState('building_diagram');
  const [docNotes, setDocNotes] = useState('');
  const [docUploading, setDocUploading] = useState(false);
  const [docError, setDocError] = useState('');
  const docFileRef = useRef<HTMLInputElement>(null);

  const loadDocuments = async (locationId: number) => {
    try {
      const r = await api.get(`/locations/${locationId}/documents`);
      setDocuments(r.data);
    } catch { setDocuments([]); }
  };

  const handleDocUpload = async (fileList: FileList | null) => {
    if (!editing || !fileList || fileList.length === 0) return;
    setDocError('');
    setDocUploading(true);
    try {
      for (const file of Array.from(fileList)) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('category', docCategory);
        fd.append('notes', docNotes);
        await api.post(`/locations/${editing.id}/documents`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      setDocNotes('');
      if (docFileRef.current) docFileRef.current.value = '';
      await loadDocuments(editing.id);
    } catch (err: any) {
      setDocError(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setDocUploading(false);
    }
  };

  const handleDocDelete = async (docId: number) => {
    if (!editing) return;
    if (!window.confirm('Delete this document?')) return;
    try {
      await api.delete(`/locations/documents/${docId}`);
      await loadDocuments(editing.id);
    } catch (err: any) {
      setDocError(err?.response?.data?.detail || 'Delete failed');
    }
  };

  // Geocoding / map state
  const [previewCoords, setPreviewCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewAreaInfo, setPreviewAreaInfo] = useState('');
  const previewMapRef = useRef<HTMLDivElement>(null);
  const previewMapInstance = useRef<any>(null);
  const previewMarkerRef = useRef<any>(null);
  const [jurisdictionState, setJurisdictionState] = useState('');
  const [jurisdictionCounty, setJurisdictionCounty] = useState('');
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>({ lat: 37.2, lng: -93.3 });

  const load = () => { api.get('/locations/all').then((r) => setLocations(r.data)).catch(() => api.get('/locations').then((r) => setLocations(r.data))); };
  useEffect(() => {
    load();
    api.get('/system-config').then((r) => {
      if (r.data.jurisdiction_state) setJurisdictionState(r.data.jurisdiction_state);
      if (r.data.jurisdiction_county) setJurisdictionCounty(r.data.jurisdiction_county);
      if (r.data.map_center_lat && r.data.map_center_lng) setMapCenter({ lat: r.data.map_center_lat, lng: r.data.map_center_lng });
    }).catch(() => {});
  }, []);

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await api.post('/locations/import-csv', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSnack(`Imported: ${r.data.created} created, ${r.data.skipped} skipped`);
      load();
    } catch { setSnack('Import failed'); }
    if (fileRef.current) fileRef.current.value = '';
  };

  const resetPreviewMap = () => {
    setPreviewCoords(null);
    setPreviewAreaInfo('');
    if (previewMapInstance.current) {
      previewMapInstance.current.remove();
      previewMapInstance.current = null;
      previewMarkerRef.current = null;
    }
  };

  const handleOpen = (loc?: LocationRecord) => {
    resetPreviewMap();
    setDocError('');
    setDocNotes('');
    setDocCategory('building_diagram');
    if (loc) {
      setEditing(loc);
      setForm({
        name: loc.name, category: loc.category, address: loc.address,
        apt_suite: loc.apt_suite, city: loc.city, state: loc.state,
        zip_code: loc.zip_code, phone: loc.phone || '', latitude: loc.latitude?.toString() || '',
        longitude: loc.longitude?.toString() || '',
        hazard_info: loc.hazard_info, additional_notes: loc.additional_notes,
      });
      loadDocuments(loc.id);
    } else {
      setEditing(null);
      setDocuments([]);
      setForm({ name: '', category: 'other', address: '', apt_suite: '', city: '', state: '', zip_code: '', phone: '', latitude: '', longitude: '', hazard_info: '', additional_notes: '' });
    }
    setOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Deactivate this location?')) return;
    await api.delete(`/locations/${id}`);
    load();
  };

  const handleSave = async () => {
    const payload = {
      ...form,
      latitude: form.latitude ? Number(form.latitude) : null,
      longitude: form.longitude ? Number(form.longitude) : null,
    };
    if (editing) {
      await api.patch(`/locations/${editing.id}`, payload);
    } else {
      await api.post('/locations', payload);
    }
    setOpen(false);
    resetPreviewMap();
    load();
  };

  // Geocoding helpers
  const nominatimSearch = async (query: string, vb?: string): Promise<any[]> => {
    const params = new URLSearchParams({ format: 'json', addressdetails: '1', q: query, limit: '5' });
    if (vb) { params.set('viewbox', vb); params.set('bounded', '0'); }
    const r = await fetch(`https://nominatim.openstreetmap.org/search?${params}`);
    return r.json();
  };

  const pickBestResult = (results: any[], centerLat: number, centerLng: number) => {
    if (results.length === 0) return null;
    if (results.length === 1) return results[0];
    let best = results[0];
    let bestDist = Math.hypot(parseFloat(best.lat) - centerLat, parseFloat(best.lon) - centerLng);
    for (let i = 1; i < results.length; i++) {
      const d = Math.hypot(parseFloat(results[i].lat) - centerLat, parseFloat(results[i].lon) - centerLng);
      if (d < bestDist) { best = results[i]; bestDist = d; }
    }
    return best;
  };

  const reverseGeocode = async (lat: number, lng: number) => {
    try {
      const revR = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=17&addressdetails=1`);
      const revData = await revR.json();
      const addr = revData?.address || {};
      const parts = [addr.neighbourhood || addr.suburb || '', addr.city || addr.town || addr.village || '', addr.county || '', addr.state || ''].filter(Boolean);
      setPreviewAreaInfo(parts.join(', '));
      // Auto-fill city/state if empty
      if (!form.city && (addr.city || addr.town || addr.village)) {
        setForm((f) => ({ ...f, city: addr.city || addr.town || addr.village || '' }));
      }
      if (!form.state && addr.state) {
        setForm((f) => ({ ...f, state: addr.state }));
      }
      if (!form.zip_code && addr.postcode) {
        setForm((f) => ({ ...f, zip_code: addr.postcode }));
      }
    } catch { /* ignore */ }
  };

  const handlePreviewAddress = async () => {
    const fullAddr = [form.address, form.city, form.state, form.zip_code].filter(Boolean).join(', ');
    if (!fullAddr) return;

    // If we already have coordinates, just show the map
    if (form.latitude && form.longitude) {
      const lat = Number(form.latitude);
      const lng = Number(form.longitude);
      setPreviewCoords({ lat, lng });
      reverseGeocode(lat, lng);
      return;
    }

    setPreviewLoading(true);
    const vb = `${mapCenter.lng - 1},${mapCenter.lat - 1},${mapCenter.lng + 1},${mapCenter.lat + 1}`;
    let result: any = null;
    try {
      // Strategy 1: Full address with jurisdiction context
      const q1 = [form.address, form.city || jurisdictionCounty, form.state || jurisdictionState].filter(Boolean).join(', ');
      const d1 = await nominatimSearch(q1, vb);
      result = pickBestResult(d1, mapCenter.lat, mapCenter.lng);
      // Strategy 2: Address + state only
      if (!result && (form.state || jurisdictionState)) {
        const q2 = `${form.address}, ${form.state || jurisdictionState}`;
        const d2 = await nominatimSearch(q2, vb);
        result = pickBestResult(d2, mapCenter.lat, mapCenter.lng);
      }
      // Strategy 3: Just the address with viewbox
      if (!result) {
        const d3 = await nominatimSearch(form.address, vb);
        result = pickBestResult(d3, mapCenter.lat, mapCenter.lng);
      }
      // Strategy 4: Without house number
      if (!result) {
        const noNum = form.address.replace(/^\d+\s+/, '');
        if (noNum !== form.address) {
          const q4 = [noNum, jurisdictionCounty, jurisdictionState].filter(Boolean).join(', ');
          const d4 = await nominatimSearch(q4, vb);
          result = pickBestResult(d4, mapCenter.lat, mapCenter.lng);
        }
      }
    } catch { /* ignore */ }

    if (result) {
      const lat = parseFloat(result.lat);
      const lng = parseFloat(result.lon);
      setForm((f) => ({ ...f, latitude: lat.toString(), longitude: lng.toString() }));
      setPreviewCoords({ lat, lng });
      const addr = result.address || {};
      const areaParts = [addr.neighbourhood || addr.suburb || '', addr.city || addr.town || addr.village || '', addr.county || '', addr.state || ''].filter(Boolean);
      setPreviewAreaInfo(areaParts.join(', '));
      // Auto-fill city/state/zip if empty
      if (!form.city && (addr.city || addr.town || addr.village)) {
        setForm((f) => ({ ...f, city: addr.city || addr.town || addr.village || '' }));
      }
      if (!form.state && addr.state) {
        setForm((f) => ({ ...f, state: addr.state }));
      }
      if (!form.zip_code && addr.postcode) {
        setForm((f) => ({ ...f, zip_code: addr.postcode }));
      }
    } else {
      setPreviewCoords({ lat: mapCenter.lat, lng: mapCenter.lng });
      setPreviewAreaInfo('Address not found \u2014 drag pin to set location');
    }
    setPreviewLoading(false);
  };

  const handleClearAddress = () => {
    setForm((f) => ({ ...f, latitude: '', longitude: '' }));
    resetPreviewMap();
  };

  // Initialize/update preview map when coords change
  useEffect(() => {
    if (!previewCoords || !previewMapRef.current) return;
    if (!previewMapInstance.current) {
      const map = L.map(previewMapRef.current, { zoomControl: true, attributionControl: false })
        .setView([previewCoords.lat, previewCoords.lng], 15);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png').addTo(map);
      previewMapInstance.current = map;
      const pinIcon = L.divIcon({
        className: '',
        iconSize: [24, 24],
        iconAnchor: [12, 24],
        html: '<div style="width:24px;height:24px;background:#2196f3;border:2px solid #fff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 2px 6px rgba(0,0,0,0.4);"></div>',
      });
      const marker = L.marker([previewCoords.lat, previewCoords.lng], { icon: pinIcon, draggable: true })
        .addTo(map);
      marker.on('dragend', async () => {
        const pos = marker.getLatLng();
        const lat = Math.round(pos.lat * 1e7) / 1e7;
        const lng = Math.round(pos.lng * 1e7) / 1e7;
        setForm((f) => ({ ...f, latitude: lat.toString(), longitude: lng.toString() }));
        setPreviewCoords({ lat, lng });
        reverseGeocode(lat, lng);
      });
      previewMarkerRef.current = marker;
    } else {
      previewMapInstance.current.setView([previewCoords.lat, previewCoords.lng], 15);
      previewMarkerRef.current?.setLatLng([previewCoords.lat, previewCoords.lng]);
    }
  }, [previewCoords]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Locations</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <input type="file" accept=".csv" ref={fileRef} hidden onChange={handleImport} />
          <Button startIcon={<Upload />} variant="outlined" onClick={() => fileRef.current?.click()}>Import CSV</Button>
          <Button startIcon={<Add />} variant="contained" onClick={() => handleOpen()}>Add Location</Button>
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Common places that dispatchers can reference with <strong>@name</strong> to auto-fill address, hazard info, and notes.
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>Hazards</TableCell>
              <TableCell>Status</TableCell>
              <TableCell width={80} />
            </TableRow>
          </TableHead>
          <TableBody>
            {locations.map((loc) => (
              <TableRow key={loc.id}>
                <TableCell sx={{ fontWeight: 700 }}>{loc.name}</TableCell>
                <TableCell><Chip size="small" label={loc.category} /></TableCell>
                <TableCell>{loc.address}{loc.city ? `, ${loc.city}` : ''}{loc.state ? `, ${loc.state}` : ''} {loc.zip_code}</TableCell>
                <TableCell>{loc.hazard_info ? <Chip size="small" icon={<Warning />} label="Yes" color="warning" /> : '\u2014'}</TableCell>
                <TableCell><Chip size="small" label={loc.is_active ? 'Active' : 'Inactive'} color={loc.is_active ? 'success' : 'default'} /></TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleOpen(loc)}><Edit fontSize="small" /></IconButton>
                  {loc.is_active && (
                    <IconButton size="small" color="error" onClick={() => handleDelete(loc.id)}><Delete fontSize="small" /></IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => { setOpen(false); resetPreviewMap(); }} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Edit Location' : 'New Location'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField fullWidth label="Location Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Walmart Supercenter" />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select value={form.category} label="Category" onChange={(e: SelectChangeEvent) => setForm({ ...form, category: e.target.value })}>
                  {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c.replace('_', ' ')}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth label="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField fullWidth label="Apt/Suite" value={form.apt_suite} onChange={(e) => setForm({ ...form, apt_suite: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField fullWidth label="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField fullWidth label="State" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField fullWidth label="Zip Code" value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField fullWidth label="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Callback number for auto-lookup" />
            </Grid>
            {/* Geocode buttons */}
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button
                  startIcon={<MyLocation />}
                  variant="outlined"
                  onClick={handlePreviewAddress}
                  disabled={previewLoading || !form.address}
                >
                  {previewLoading ? 'Geocoding...' : 'Preview Address'}
                </Button>
                {previewCoords && (
                  <Button
                    startIcon={<Clear />}
                    variant="outlined"
                    color="warning"
                    onClick={handleClearAddress}
                  >
                    Clear Address
                  </Button>
                )}
                {previewCoords && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    Drag pin to fine-tune location
                  </Typography>
                )}
              </Box>
            </Grid>
            {/* Map preview */}
            {previewCoords && (
              <Grid size={{ xs: 12 }}>
                <Box
                  ref={previewMapRef}
                  sx={{ height: 250, borderRadius: 1, overflow: 'hidden', border: '1px solid #555' }}
                />
                {previewAreaInfo && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {previewAreaInfo}
                  </Typography>
                )}
              </Grid>
            )}
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField fullWidth label="Latitude" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField fullWidth label="Longitude" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth multiline rows={2} label="Hazard Information" value={form.hazard_info} onChange={(e) => setForm({ ...form, hazard_info: e.target.value })} placeholder="Known aggressive dog, chemical storage, weapons on premises, etc." />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth multiline rows={2} label="Additional Notes" value={form.additional_notes} onChange={(e) => setForm({ ...form, additional_notes: e.target.value })} placeholder="Gate code: 1234, use rear entrance, after-hours contact: 555-1234" />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                <Description fontSize="small" /> Premise Documents
              </Typography>
              {!editing ? (
                <Typography variant="caption" color="text.secondary">
                  Save this location first, then reopen it to attach building diagrams, hazard plans, and other documents.
                </Typography>
              ) : (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    PDFs or images (PNG, JPG, GIF, WEBP), up to 25 MB each. These are linked on calls created at this premise for Dispatch &amp; MDT users.
                  </Typography>
                  {docError && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setDocError('')}>{docError}</Alert>}
                  <Grid container spacing={1} sx={{ mb: 1, alignItems: 'center' }}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Category</InputLabel>
                        <Select label="Category" value={docCategory} onChange={(e: SelectChangeEvent) => setDocCategory(e.target.value)}>
                          {DOC_CATEGORIES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 5 }}>
                      <TextField fullWidth size="small" label="Notes (e.g. 2nd Floor Diagram)" value={docNotes} onChange={(e) => setDocNotes(e.target.value)} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 3 }}>
                      <Button fullWidth variant="outlined" startIcon={<Upload />} disabled={docUploading} onClick={() => docFileRef.current?.click()}>
                        {docUploading ? 'Uploading…' : 'Upload'}
                      </Button>
                      <input ref={docFileRef} type="file" hidden accept="application/pdf,image/png,image/jpeg,image/gif,image/webp"
                        onChange={(e) => handleDocUpload(e.target.files)} />
                    </Grid>
                  </Grid>
                  {documents.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">No documents uploaded yet.</Typography>
                  ) : (
                    <TableContainer component={Paper} sx={{ maxHeight: 220 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Category</TableCell>
                            <TableCell>File / Notes</TableCell>
                            <TableCell>Uploaded</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {documents.map((d) => (
                            <TableRow key={d.id}>
                              <TableCell><Chip size="small" label={DOC_CATEGORY_LABELS[d.category] || d.category} /></TableCell>
                              <TableCell>
                                <Typography variant="body2">{d.original_filename}</Typography>
                                {d.notes && <Typography variant="caption" color="text.secondary">{d.notes}</Typography>}
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{formatSize(d.size_bytes)}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption">{d.uploaded_at ? new Date(d.uploaded_at).toLocaleString() : '—'}</Typography>
                                {d.uploaded_by && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>by {d.uploaded_by}</Typography>}
                              </TableCell>
                              <TableCell align="right">
                                <IconButton size="small" title="View" onClick={() => window.open(d.view_url, '_blank', 'noopener')}><OpenInNew fontSize="small" /></IconButton>
                                <IconButton size="small" title="Delete" color="error" onClick={() => handleDocDelete(d.id)}><Delete fontSize="small" /></IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpen(false); resetPreviewMap(); }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack('')}>
        <Alert severity="info" onClose={() => setSnack('')}>{snack}</Alert>
      </Snackbar>
    </Box>
  );
}
